/** 首页结构化数据（Phase 2 + PDF / gbwta 参考） */

import { FEATURE_CARD_IMAGES } from './home-images';

export const APP_INFO = [
	{ label: 'Version', value: '18.80' },
	{ label: 'Size', value: '82 MB' },
	{ label: 'Android', value: '4.4+' },
	{ label: 'Last Updated', value: '2026' },
];

/** Hero 下方快捷卖点（对标竞品 “New Features” 一行入口） */
export const FEATURE_PILLS = [
	{ label: 'Auto Reply', hint: 'Set replies for busy hours' },
	{ label: 'Hide Online Status', hint: 'Read chats with less visibility' },
	{ label: 'Status Download', hint: 'Save photos & videos from status' },
	{ label: 'DND / Airplane Mode', hint: 'Quiet GBWhatsApp without killing Wi‑Fi' },
	{ label: 'Media Sharing', hint: 'Larger files, less compression' },
];

/** 三大功能 Spotlight（对标 Ghost / Broadcast / Call privacy 版块） */
export const SPOTLIGHT_BLOCKS = [
	{
		title: 'Stay Low‑Profile While You Chat',
		text: 'Many builds bundle “ghost” or privacy modes: hide online, read messages without blue ticks, and preview statuses with fewer traces. Paths vary by developer—open Settings → Privacy and explore hide options from a fresh 18.80 install before turning everything on at once.',
	},
	{
		title: 'Broadcast Without Building a Group',
		text: 'Send one update to many contacts privately—each person receives it like a direct message. Useful for invites or shop hours. Avoid blasting identical text to hundreds of new numbers in minutes; that pattern triggers spam detection on any client.',
	},
	{
		title: 'Call & Contact Controls',
		text: 'Block calls from specific senders, mute threads, or route alerts with custom notifications. Pair these tools with our anti-ban guide so aggressive privacy tweaks do not coincide with account warm-up right after login.',
	},
];

/** 安装前检查（对标 “Preconditions”） */
export const PRE_DOWNLOAD_CHECKS = [
	'Stable Wi‑Fi or data for an ~82 MB file',
	'At least ~200 MB free storage (APK + cache)',
	'Android 4.4+ (newer devices get the smoothest login)',
	'Battery above 20% so install is not cut off',
	'Unknown sources enabled for your browser or Files app',
	'Google Drive or local backup if you are migrating numbers',
];

export const PROS_LIST = [
	'Rich privacy toggles (ticks, typing, recording, last seen)',
	'Themes, fonts, and UI tweaks the official app does not ship',
	'Larger attachments and status tools on supported builds',
	'Dual-number setups possible when the build allows cloning',
	'Active mod communities—fast iterations when login rules change',
];

export const CONS_LIST = [
	'Not on Google Play—manual APK updates only',
	'Higher ban risk than official WhatsApp—Meta may restrict unofficial clients',
	'APK trust is your responsibility—avoid repacks from random mirrors',
	'Feature names/paths differ by packager—verify against your About screen',
	'No Meta support channel—use guides like ours + developer notes',
];

export const FEATURE_CARDS = [
	{
		image: FEATURE_CARD_IMAGES.privacy,
		imageAlt: 'GB WhatsApp Privacy Features — hide blue ticks, last seen, typing indicator',
		title: 'Privacy Features',
		text: 'Hide blue ticks, last seen, typing indicator, and forward tags.',
	},
	{
		image: FEATURE_CARD_IMAGES.themes,
		imageAlt: 'GB WhatsApp Themes and UI customization',
		title: 'Themes & UI',
		text: 'Custom themes, fonts, and chat bubbles beyond stock WhatsApp.',
	},
	{
		image: FEATURE_CARD_IMAGES.media,
		imageAlt: 'GB WhatsApp Media and Files — larger uploads',
		title: 'Media & Files',
		text: 'Send larger files, sharper images, and longer videos.',
	},
	{
		image: FEATURE_CARD_IMAGES.status,
		imageAlt: 'GB WhatsApp Status Tools',
		title: 'Status Tools',
		text: 'Download status media and write longer captions.',
	},
	{
		image: FEATURE_CARD_IMAGES.dnd,
		imageAlt: 'GB WhatsApp DND and chat locks',
		title: 'DND & Locks',
		text: 'DND for the mod only, plus chat locks and hidden chats.',
	},
	{
		image: FEATURE_CARD_IMAGES.dual,
		imageAlt: 'GB WhatsApp Dual Accounts on one phone',
		title: 'Dual Accounts',
		text: 'Run a second number on one phone when your build allows.',
	},
];

export const COMPARISON_ROWS = [
	{ feature: 'Hide blue ticks', official: '❌', gb: '✅' },
	{ feature: 'Hide online / last seen', official: 'Limited', gb: '✅' },
	{ feature: 'Custom themes', official: '❌', gb: '✅' },
	{ feature: 'Send files up to 1GB+', official: '❌', gb: '✅' },
	{ feature: 'Anti-delete messages', official: '❌', gb: '✅' },
	{ feature: 'Official Play Store', official: '✅', gb: '❌' },
	{ feature: 'End-to-end encryption (official)', official: '✅', gb: 'Varies by build' },
	{ feature: 'Ban risk', official: 'Low', gb: 'Higher (unofficial)' },
];

export const HOME_FAQ_SNIPPETS = [
	{
		q: 'How do I update GB WhatsApp?',
		a: 'The Play Store will not auto-update a mod. Download the newest APK from our download page, back up chats first, then install on top of the old version if signatures match. Follow the GBWaps update guide if you get a login loop.',
	},
	{
		q: 'What is different from official WhatsApp?',
		a: 'Official WhatsApp optimizes for policy compliance and Play distribution. GBWhatsApp adds privacy switches, themes, larger media limits, and power-user tools. You trade Meta support for flexibility—and you must manage ban risk yourself.',
	},
	{
		q: 'Can I run GBWhatsApp and WhatsApp on one phone?',
		a: 'Yes, usually with two different numbers (official for work, mod for personal, or the reverse). Expect extra battery use and occasional OEM battery-killer quirks. One number generally cannot stay logged into both clones at once without specialized dual-app setups.',
	},
];

export const HOME_FAQ = [
	{
		q: 'Is GBWhatsApp safe to use?',
		a: 'GBWhatsApp is an unofficial mod, not published by Meta. Safety depends on where you download the APK, which developer build you use, and whether you keep it updated. Stick to sources you trust, scan files, and avoid sharing banking or work-critical accounts on a modded client.',
	},
	{
		q: 'Will I get banned for using GBWhatsApp?',
		a: 'Meta can detect unofficial clients. Many users run mods for years, but bans still happen—especially after outdated builds or aggressive privacy settings. Follow our anti-ban guide, avoid mass forwarding, and back up chats before switching.',
	},
	{
		q: 'What is the latest GBWhatsApp version?',
		a: 'This guide tracks v18.80 (82 MB, Android 4.4+). New builds ship often; always match the version on our download page with the file you install. Older APKs may fail login or lack recent security patches from the mod developer.',
	},
	{
		q: 'How do I install GBWhatsApp on Android?',
		a: 'Download the APK, enable Install unknown apps for your browser or file manager, open the file, and complete setup. You will verify your number again. See our step-by-step install guide for screenshots and troubleshooting.',
	},
	{
		q: 'Can I use GBWhatsApp and official WhatsApp together?',
		a: 'Usually you pick one app per number. Some users keep official WhatsApp for work and GBWhatsApp for personal use on the same phone with different numbers. Do not expect both apps to share the same verified session without cloning tools.',
	},
	{
		q: 'How do I update GBWhatsApp without losing chats?',
		a: 'Back up to local storage or Google Drive inside WhatsApp settings first. Install the new APK over the old one without uninstalling. If login fails, restore from backup or read our update guide before clearing data.',
	},
	{
		q: 'Why is GBWhatsApp not installing?',
		a: 'Common causes: corrupted download, blocked unknown sources, low storage, or Android version below 4.4. Delete partial installs, re-download from our download page, and check troubleshooting for error codes.',
	},
	{
		q: 'Does GBWhatsApp work on iPhone?',
		a: 'No. GBWhatsApp targets Android APK installs. iOS does not allow this type of mod without jailbreak, which we do not recommend. Use official WhatsApp on iPhone.',
	},
	{
		q: 'Where should I download GBWhatsApp APK?',
		a: 'Use our download hub for file size, version hash notes, and safety reminders. Avoid random mirror sites that bundle adware. Compare checksums when the developer publishes them.',
	},
	{
		q: 'What features does GBWhatsApp add?',
		a: 'Privacy toggles, themes, extended media limits, status download, message scheduling on some builds, and more. Our features page lists each option and how it differs from stock WhatsApp.',
	},
];

export const SOFTWARE_APPLICATION_SCHEMA = {
	'@context': 'https://schema.org',
	'@type': 'SoftwareApplication',
	name: 'GBWhatsApp',
	applicationCategory: 'CommunicationApplication',
	operatingSystem: 'Android',
	offers: {
		'@type': 'Offer',
		price: '0',
		priceCurrency: 'USD',
	},
	softwareVersion: '18.80',
};

export const FAQ_PAGE_SCHEMA = {
	'@context': 'https://schema.org',
	'@type': 'FAQPage',
	mainEntity: HOME_FAQ.map((item) => ({
		'@type': 'Question',
		name: item.q,
		acceptedAnswer: {
			'@type': 'Answer',
			text: item.a,
		},
	})),
};
